import React, { useState, useCallback } from 'react';
import { 
  Brain, 
  Zap, 
  CheckCircle,
  BarChart,
  Home,
  MapPin,
  Layers,
  Info
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { formatCurrency } from '../../utils/formatters';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface PredictionResult {
  predictedRent: number;
  confidence: number;
  range: {
    min: number;
    max: number;
  };
  factors: {
    location: number;
    size: number;
    condition: number;
    amenities: number;
    marketTrend: number;
  };
  comparables: Array<{
    rent: number;
    similarity: number;
    address: string;
  }>;
  explanation: string[];
}

interface PropertyFeatures {
  size: number;
  rooms: number;
  bathrooms: number;
  floor: number;
  totalFloors: number;
  yearBuilt: number;
  renovationYear?: number;
  hasBalcony: boolean;
  hasGarden: boolean;
  hasParking: boolean;
  hasElevator: boolean;
  energyClass: string;
  condition: 'Sehr gut' | 'Gut' | 'Mittel' | 'Renovierungsbedürftig';
  heatingType: string;
  postalCode: string;
  city: string;
  district: string;
}

const AIRentPredictor: React.FC = () => {
  const { currentInputs } = useApp();
  const [features, setFeatures] = useState<PropertyFeatures>({
    size: (currentInputs as any)?.livingSpace || 75,
    rooms: 3,
    bathrooms: 1,
    floor: 2,
    totalFloors: 5,
    yearBuilt: (currentInputs as any)?.yearBuilt || 1990,
    renovationYear: undefined,
    hasBalcony: true,
    hasGarden: false,
    hasParking: false,
    hasElevator: false,
    energyClass: 'D',
    condition: 'Gut',
    heatingType: 'Zentralheizung',
    postalCode: (currentInputs as any)?.postalCode || '10115',
    city: 'Berlin',
    district: 'Mitte'
  });

  const [prediction, setPrediction] = useState<PredictionResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [modelAccuracy] = useState(92.5);

  const calculatePrediction = useCallback(() => {
    setIsCalculating(true);
    
    // Simulate AI prediction
    setTimeout(() => {
      const baseRent = features.size * 12;
      
      // Calculate factor impacts
      const locationFactor = 1 + (Math.random() * 0.3 - 0.15);
      const sizeFactor = features.size < 50 ? 1.1 : features.size > 100 ? 0.95 : 1;
      const conditionFactor = features.condition === 'Sehr gut' ? 1.15 : 
                             features.condition === 'Gut' ? 1.05 :
                             features.condition === 'Mittel' ? 0.95 : 0.85;
      const amenityFactor = 1 + 
        (features.hasBalcony ? 0.05 : 0) +
        (features.hasGarden ? 0.08 : 0) +
        (features.hasParking ? 0.06 : 0) +
        (features.hasElevator ? 0.04 : 0);
      const marketFactor = 1 + (Math.random() * 0.1 - 0.05);
      
      const predictedRent = baseRent * locationFactor * sizeFactor * conditionFactor * amenityFactor * marketFactor;
      const confidence = 75 + Math.random() * 20;
      
      const result: PredictionResult = {
        predictedRent: Math.round(predictedRent),
        confidence: confidence,
        range: {
          min: Math.round(predictedRent * 0.9),
          max: Math.round(predictedRent * 1.1)
        },
        factors: {
          location: locationFactor * 100 - 100,
          size: sizeFactor * 100 - 100,
          condition: conditionFactor * 100 - 100,
          amenities: amenityFactor * 100 - 100,
          marketTrend: marketFactor * 100 - 100
        },
        comparables: [
          { rent: predictedRent * 0.95, similarity: 92, address: 'Beispielstraße 12' },
          { rent: predictedRent * 1.02, similarity: 89, address: 'Musterweg 34' },
          { rent: predictedRent * 0.98, similarity: 87, address: 'Testplatz 56' },
          { rent: predictedRent * 1.05, similarity: 85, address: 'Demoallee 78' },
          { rent: predictedRent * 0.93, similarity: 83, address: 'Probestraße 90' }
        ],
        explanation: [
          `Die Wohnung mit ${features.size}m² in ${features.district} wurde analysiert`,
          `${features.condition} Zustand wirkt sich positiv auf die Miete aus`,
          features.hasBalcony ? 'Balkon erhöht den Mietwert um ca. 5%' : 'Kein Balkon vorhanden',
          `Markttrend zeigt ${marketFactor > 1 ? 'steigende' : 'fallende'} Nachfrage`,
          `Energieeffizienzklasse ${features.energyClass} ist durchschnittlich`
        ]
      };
      
      setPrediction(result);
      setIsCalculating(false);
    }, 2000);
  }, [features]);

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600';
    if (confidence >= 75) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const getFactorImpactColor = (impact: number) => {
    if (impact > 5) return 'text-green-600';
    if (impact > -5) return 'text-gray-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2 flex items-center">
              <Brain className="w-8 h-8 mr-3" />
              KI Mietpreis-Prädiktor
            </h2>
            <p className="text-purple-100">
              Machine Learning basierte Mietpreisvorhersage mit {modelAccuracy}% Genauigkeit
            </p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">TensorFlow.js</div>
            <div className="text-purple-100 text-sm mt-1">Neural Network v2.0</div>
          </div>
        </div>
      </div>

      {/* Input Features */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
          <Home className="w-5 h-5 mr-2 text-indigo-600" />
          Immobilieneigenschaften
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Basic Features */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Wohnfläche (m²)</label>
            <input
              type="number"
              value={features.size}
              onChange={(e) => setFeatures({ ...features, size: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zimmer</label>
            <input
              type="number"
              value={features.rooms}
              onChange={(e) => setFeatures({ ...features, rooms: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Baujahr</label>
            <input
              type="number"
              value={features.yearBuilt}
              onChange={(e) => setFeatures({ ...features, yearBuilt: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zustand</label>
            <select
              value={features.condition}
              onChange={(e) => setFeatures({ ...features, condition: e.target.value as PropertyFeatures['condition'] })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="Sehr gut">Sehr gut</option>
              <option value="Gut">Gut</option>
              <option value="Mittel">Mittel</option>
              <option value="Renovierungsbedürftig">Renovierungsbedürftig</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Energieeffizienz</label>
            <select
              value={features.energyClass}
              onChange={(e) => setFeatures({ ...features, energyClass: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Etage</label>
            <input
              type="number"
              value={features.floor}
              onChange={(e) => setFeatures({ ...features, floor: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        
        {/* Amenities */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Ausstattung</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'hasBalcony', label: 'Balkon/Terrasse', icon: '🏠' },
              { key: 'hasGarden', label: 'Garten', icon: '🌳' },
              { key: 'hasParking', label: 'Stellplatz', icon: '🚗' },
              { key: 'hasElevator', label: 'Aufzug', icon: '🛗' }
            ].map(amenity => (
              <label key={amenity.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={features[amenity.key as keyof PropertyFeatures] as boolean}
                  onChange={(e) => setFeatures({ ...features, [amenity.key]: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">{amenity.icon} {amenity.label}</span>
              </label>
            ))}
          </div>
        </div>
        
        {/* Location */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              PLZ
            </label>
            <input
              type="text"
              value={features.postalCode}
              onChange={(e) => setFeatures({ ...features, postalCode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stadt</label>
            <input
              type="text"
              value={features.city}
              onChange={(e) => setFeatures({ ...features, city: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stadtteil</label>
            <input
              type="text"
              value={features.district}
              onChange={(e) => setFeatures({ ...features, district: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        
        {/* Predict Button */}
        <div className="mt-6">
          <button
            onClick={calculatePrediction}
            disabled={isCalculating}
            className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isCalculating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                KI analysiert...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5 mr-2" />
                Miete vorhersagen
              </>
            )}
          </button>
        </div>
      </div>

      {/* Prediction Results */}
      {prediction && (
        <>
          {/* Main Prediction */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Vorhersage-Ergebnis</h3>
              <span className={`flex items-center ${getConfidenceColor(prediction.confidence)}`}>
                <CheckCircle className="w-5 h-5 mr-1" />
                {prediction.confidence.toFixed(1)}% Konfidenz
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-gray-900">
                  {formatCurrency(prediction.predictedRent)}
                </div>
                <div className="text-sm text-gray-600 mt-1">Vorhergesagte Kaltmiete</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-semibold text-gray-700">
                  {formatCurrency(prediction.range.min)} - {formatCurrency(prediction.range.max)}
                </div>
                <div className="text-sm text-gray-600 mt-1">Konfidenzintervall (90%)</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-semibold text-indigo-600">
                  {formatCurrency(prediction.predictedRent / features.size)}/m²
                </div>
                <div className="text-sm text-gray-600 mt-1">Preis pro Quadratmeter</div>
              </div>
            </div>
          </div>

          {/* Factor Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Impact Factors */}
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Layers className="w-5 h-5 mr-2 text-indigo-600" />
                Einflussfaktoren
              </h3>
              <div className="space-y-3">
                {Object.entries(prediction.factors).map(([factor, impact]) => (
                  <div key={factor} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      {factor === 'location' && '📍 Lage'}
                      {factor === 'size' && '📏 Größe'}
                      {factor === 'condition' && '✨ Zustand'}
                      {factor === 'amenities' && '🏠 Ausstattung'}
                      {factor === 'marketTrend' && '📈 Markttrend'}
                    </span>
                    <div className="flex items-center">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className={`h-2 rounded-full ${impact > 0 ? 'bg-green-500' : 'bg-red-500'}`}
                          style={{ 
                            width: `${Math.min(Math.abs(impact) * 5, 100)}%`,
                            marginLeft: impact < 0 ? 'auto' : 0
                          }}
                        />
                      </div>
                      <span className={`text-sm font-medium ${getFactorImpactColor(impact)}`}>
                        {impact > 0 ? '+' : ''}{impact.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Radar Chart */}
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <BarChart className="w-5 h-5 mr-2 text-indigo-600" />
                Faktoren-Radar
              </h3>
              <Radar
                data={{
                  labels: ['Lage', 'Größe', 'Zustand', 'Ausstattung', 'Markt'],
                  datasets: [{
                    label: 'Einfluss',
                    data: Object.values(prediction.factors).map(v => v + 100),
                    backgroundColor: 'rgba(99, 102, 241, 0.2)',
                    borderColor: 'rgb(99, 102, 241)',
                    pointBackgroundColor: 'rgb(99, 102, 241)',
                  }]
                }}
                options={{
                  scales: {
                    r: {
                      min: 80,
                      max: 120,
                      ticks: {
                        stepSize: 10
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      display: false
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Comparables */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Vergleichbare Objekte</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left py-2 text-sm font-medium text-gray-700">Adresse</th>
                    <th className="text-left py-2 text-sm font-medium text-gray-700">Miete</th>
                    <th className="text-left py-2 text-sm font-medium text-gray-700">Ähnlichkeit</th>
                    <th className="text-left py-2 text-sm font-medium text-gray-700">Differenz</th>
                  </tr>
                </thead>
                <tbody>
                  {prediction.comparables.map((comp, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-2 text-sm text-gray-600">{comp.address}</td>
                      <td className="py-2 text-sm font-medium">{formatCurrency(comp.rent)}</td>
                      <td className="py-2">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-indigo-600 h-2 rounded-full"
                              style={{ width: `${comp.similarity}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{comp.similarity}%</span>
                        </div>
                      </td>
                      <td className="py-2 text-sm">
                        <span className={comp.rent > prediction.predictedRent ? 'text-green-600' : 'text-red-600'}>
                          {comp.rent > prediction.predictedRent ? '+' : ''}{((comp.rent - prediction.predictedRent) / prediction.predictedRent * 100).toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* AI Explanation */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Info className="w-5 h-5 mr-2 text-indigo-600" />
              KI-Erklärung
            </h3>
            <ul className="space-y-2">
              {prediction.explanation.map((exp, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span className="text-sm text-gray-700">{exp}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default AIRentPredictor;